import { getLocaleMeta } from "@/lib/i18n/locales";

/**
 * The class schedule maker's model: times, overlap detection, side-by-side layout of
 * clashing classes, the colour palette, and validation of what comes back from storage.
 *
 * Days are Monday-first positions (0 = Monday … 6 = Sunday) and times are minutes after
 * midnight, which keeps a timetable free of any calendar date or time zone: a class at
 * 09:00 on Tuesdays is just that.
 */

export interface ClassEntry {
  id: string;
  name: string;
  /** Monday-first day positions. */
  days: number[];
  /** Minutes after midnight. */
  start: number;
  end: number;
  location: string;
  /** Index into `CLASS_COLORS`. */
  color: number;
}

/**
 * Block colours: a light tint to fill, the strong Okabe–Ito hue for the edge, and one
 * dark ink, so every block reads in colour, in greyscale and with colour-vision
 * deficiency. Hex values because an exported SVG or PNG carries no stylesheet.
 */
export const CLASS_COLORS = [
  { fill: "#D6E9F5", edge: "#0072B2" },
  { fill: "#FBEBC7", edge: "#E69F00" },
  { fill: "#CCEFE5", edge: "#009E73" },
  { fill: "#F7DCC7", edge: "#D55E00" },
  { fill: "#DDF0FB", edge: "#56B4E9" },
  { fill: "#F4E1EC", edge: "#CC79A7" },
  { fill: "#FBF7CF", edge: "#C9B800" },
  { fill: "#E5E7EB", edge: "#6B7280" },
] as const;

export const CLASS_INK = "#1F1F1F";

const TIME = /^(\d{1,2}):(\d{2})$/;

/** `HH:MM` (what `<input type="time">` holds) → minutes after midnight. */
export function parseTime(value: string): number | null {
  const match = TIME.exec(value.trim());
  if (!match) {
    return null;
  }
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) {
    return null;
  }
  return hours * 60 + minutes;
}

/** Minutes after midnight → `HH:MM`. */
export function timeValue(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  return `${String(hours).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`;
}

export interface Overlap {
  day: number;
  first: ClassEntry;
  second: ClassEntry;
  /** The clashing span. */
  start: number;
  end: number;
}

/** Classes on one day, earliest first. */
export function classesOnDay(
  classes: readonly ClassEntry[],
  day: number
): ClassEntry[] {
  return classes
    .filter((entry) => entry.days.includes(day))
    .sort((a, b) => a.start - b.start || a.end - b.end);
}

/** Every pair of classes that share time on a day. Touching (10:00 end, 10:00 start) is fine. */
export function findOverlaps(
  classes: readonly ClassEntry[],
  days: readonly number[]
): Overlap[] {
  const overlaps: Overlap[] = [];
  for (const day of days) {
    const today = classesOnDay(classes, day);
    for (let i = 0; i < today.length; i += 1) {
      for (let j = i + 1; j < today.length; j += 1) {
        const first = today[i];
        const second = today[j];
        if (second.start < first.end && first.start < second.end) {
          overlaps.push({
            day,
            first,
            second,
            start: Math.max(first.start, second.start),
            end: Math.min(first.end, second.end),
          });
        }
      }
    }
  }
  return overlaps;
}

export interface PlacedClass {
  entry: ClassEntry;
  /** Column within its clash group, from 0. */
  lane: number;
  /** Columns in its clash group; 1 when it clashes with nothing. */
  lanes: number;
}

/**
 * Lay out one day so clashing classes sit side by side.
 *
 * Classes are grouped into runs that overlap one another, directly or through a chain;
 * within a run each class takes the first column that is free by its start time. A class
 * that clashes with nothing keeps the full width.
 */
export function layoutDay(classes: readonly ClassEntry[]): PlacedClass[] {
  const sorted = [...classes].sort(
    (a, b) => a.start - b.start || a.end - b.end
  );
  const placed: PlacedClass[] = [];
  let group: PlacedClass[] = [];
  let laneEnds: number[] = [];
  let groupEnd = -1;
  const closeGroup = () => {
    for (const item of group) {
      item.lanes = laneEnds.length;
    }
    placed.push(...group);
    group = [];
    laneEnds = [];
  };
  for (const entry of sorted) {
    if (group.length > 0 && entry.start >= groupEnd) {
      closeGroup();
    }
    let lane = laneEnds.findIndex((end) => end <= entry.start);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(entry.end);
    } else {
      laneEnds[lane] = entry.end;
    }
    group.push({ entry, lane, lanes: 1 });
    groupEnd = group.length === 1 ? entry.end : Math.max(groupEnd, entry.end);
  }
  closeGroup();
  return placed;
}

/**
 * The hours the grid shows: the chosen range, widened to whole hours so no class falls
 * off the top or bottom.
 */
export function visibleHours(
  classes: readonly ClassEntry[],
  fromHour: number,
  toHour: number
): { from: number; to: number } {
  let from = Math.min(fromHour, toHour - 1);
  let to = Math.max(toHour, fromHour + 1);
  for (const entry of classes) {
    from = Math.min(from, Math.floor(entry.start / 60));
    to = Math.max(to, Math.ceil(entry.end / 60));
  }
  return { from: Math.max(0, from), to: Math.min(24, to) };
}

export interface SavedSchedule {
  days: number[];
  fromHour: number;
  toHour: number;
  /** Show times on a 24-hour clock. */
  clock24: boolean;
  classes: ClassEntry[];
}

function isDayList(value: unknown): value is number[] {
  return (
    Array.isArray(value) &&
    value.every((day) => Number.isInteger(day) && day >= 0 && day <= 6)
  );
}

function isMinute(value: unknown): value is number {
  return (
    Number.isInteger(value) &&
    (value as number) >= 0 &&
    (value as number) <= 1440
  );
}

function parseClass(raw: unknown): ClassEntry | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const valid =
    typeof record.id === "string" &&
    typeof record.name === "string" &&
    typeof record.location === "string" &&
    isDayList(record.days) &&
    isMinute(record.start) &&
    isMinute(record.end) &&
    (record.end as number) > (record.start as number) &&
    Number.isInteger(record.color);
  if (!valid) {
    return null;
  }
  return {
    id: record.id as string,
    name: (record.name as string).slice(0, 80),
    location: (record.location as string).slice(0, 80),
    days: [...new Set(record.days as number[])].sort((a, b) => a - b),
    start: record.start as number,
    end: record.end as number,
    color: Math.abs(record.color as number) % CLASS_COLORS.length,
  };
}

/** Validate a saved timetable; anything malformed is dropped rather than trusted. */
export function parseSavedSchedule(raw: unknown): SavedSchedule | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }
  const record = raw as Record<string, unknown>;
  if (
    !(
      isDayList(record.days) &&
      Number.isInteger(record.fromHour) &&
      Number.isInteger(record.toHour) &&
      Array.isArray(record.classes)
    )
  ) {
    return null;
  }
  const fromHour = Math.min(23, Math.max(0, record.fromHour as number));
  const toHour = Math.min(24, Math.max(fromHour + 1, record.toHour as number));
  return {
    days: [...new Set(record.days as number[])].sort((a, b) => a - b),
    fromHour,
    toHour,
    clock24: record.clock24 === true,
    classes: (record.classes as unknown[])
      .map(parseClass)
      .filter((entry): entry is ClassEntry => entry !== null),
  };
}

/**
 * Site locales whose own convention is the 12-hour clock (9:00 AM, 오전 9:00, 9:00 ص).
 * Every other site locale writes 14:30. Spelled out, not read from `Intl` at render
 * time, so the server render and the browser always agree on the starting state whatever
 * ICU build each one carries; a test checks the table against `Intl` for every locale.
 */
const TWELVE_HOUR_LOCALES: ReadonlySet<string> = new Set([
  "en",
  "ar",
  "hi",
  "ko",
]);

/** BCP 47 tag for a site locale, falling back to English for an unknown code. */
function clockTag(locale: string): string {
  return getLocaleMeta(locale)?.hreflang ?? "en";
}

/**
 * Whether readers of this locale expect a 24-hour clock by default (German 14:30,
 * French 14:30, Russian 14:30) rather than 2:30 PM. Only the starting state: the 24-hour
 * checkbox still lets anyone switch, and a timetable saved in storage keeps its choice.
 */
export function prefers24HourClock(locale: string): boolean {
  if (getLocaleMeta(locale)) {
    return !TWELVE_HOUR_LOCALES.has(locale);
  }
  try {
    const cycle = new Intl.DateTimeFormat(clockTag(locale), {
      hour: "numeric",
    }).resolvedOptions().hourCycle;
    return cycle === "h23" || cycle === "h24";
  } catch {
    return false;
  }
}

/**
 * Minutes after midnight in the reader's clock style.
 *
 * Built on a fixed UTC date so no time zone can shift the hour; `clock24` chooses 14:30
 * over 2:30 PM regardless of the locale's default. Digits are always Western (`latn`),
 * as on the rest of the site and in the page copy, even in Arabic.
 */
export function timeFormatter(
  locale: string,
  clock24: boolean
): (minutes: number) => string {
  const format = new Intl.DateTimeFormat(clockTag(locale), {
    hour: "numeric",
    minute: "2-digit",
    hourCycle: clock24 ? "h23" : "h12",
    numberingSystem: "latn",
    timeZone: "UTC",
  });
  return (minutes) => format.format(new Date(Date.UTC(2024, 0, 1, 0, minutes)));
}

/** What is wrong with a class before it can be added, if anything. */
export type DraftProblem = "needName" | "needDay" | "needTimes";

export function checkClassDraft(draft: {
  name: string;
  days: readonly boolean[];
  start: string;
  end: string;
}): DraftProblem | null {
  if (draft.name.trim() === "") {
    return "needName";
  }
  if (!draft.days.some(Boolean)) {
    return "needDay";
  }
  const start = parseTime(draft.start);
  const end = parseTime(draft.end);
  if (start === null || end === null || end <= start) {
    return "needTimes";
  }
  return null;
}

/** A saved timetable from its stored JSON text; null when absent or unreadable. */
export function readSavedSchedule(raw: string | null): SavedSchedule | null {
  if (!raw) {
    return null;
  }
  try {
    return parseSavedSchedule(JSON.parse(raw));
  } catch {
    return null;
  }
}
